import api, { post } from "../api";
import { API_ROUTES } from "../routes";

/**
 * Response from custom login endpoint
 */
export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresIn: number;
}

/**
 * Error response from login endpoint
 */
export interface LoginError {
  error: string;
  message: string;
}

/**
 * Custom login with email and password
 * Uses Cognito USER_PASSWORD_AUTH flow via backend
 */
export const login = async (
  email: string,
  password: string,
  recaptchaToken: string
): Promise<LoginResponse> => {
  const response = await api.post(API_ROUTES.LOGIN, { email, password, recaptchaToken });
  return response.data;
};

export const fetchUserInfo = async (accessToken: string) => {
  const response = await api.get(API_ROUTES.USER_INFO, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  return response.data;
};

export const storeTokens = async (
  accessToken: string,
  refreshToken: string
) => {
  const response = await post(
    API_ROUTES.STORE_TOKEN,
    { accessToken, refreshToken },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

export const clearCookies = async () => {
  const response = await api.get(API_ROUTES.CLEAR_COOKIES, {});
  return response.data;
};

/**
 * Challenge response from login (e.g., NEW_PASSWORD_REQUIRED)
 */
export interface AuthChallengeResponse {
  challengeName: string;
  session: string;
}

/**
 * Initiate forgot password flow
 * Always returns 200 to avoid leaking email existence
 */
export const forgotPassword = async (
  email: string,
  recaptchaToken: string
): Promise<void> => {
  await api.post(API_ROUTES.FORGOT_PASSWORD, { email, recaptchaToken });
};

/**
 * Confirm forgot password with verification code and new password
 */
export const confirmForgotPassword = async (
  email: string,
  confirmationCode: string,
  newPassword: string
): Promise<void> => {
  await api.post(API_ROUTES.CONFIRM_FORGOT_PASSWORD, {
    email,
    confirmationCode,
    newPassword,
  });
};

/**
 * Respond to auth challenge (e.g., NEW_PASSWORD_REQUIRED)
 */
export const respondToChallenge = async (
  challengeName: string,
  session: string,
  newPassword: string,
  email: string
): Promise<LoginResponse> => {
  const response = await api.post(API_ROUTES.RESPOND_CHALLENGE, {
    challengeName,
    session,
    newPassword,
    email,
  });
  return response.data;
};

export type AppKey = "gosafe" | "samai" | "predict" | "nexalert" | "internal";


export const getAppUrl = (key: AppKey): string | null => {
  const mapping: Record<AppKey, string | undefined> = {
    gosafe: import.meta.env.VITE_APP_URL_GOSAFE,
    samai: import.meta.env.VITE_APP_URL_SAMAI,
    predict: import.meta.env.VITE_APP_URL_PREDICT,
    nexalert: import.meta.env.VITE_APP_URL_NEXALERT,
    internal: import.meta.env.VITE_APP_URL_INTERNAL,
  };

  const url = mapping[key];
  if (!url) {
    console.warn(`No URL found in env for app key: ${key}`);
    return null;
  }

  return url;
};

/**
 * Authenticate with Predict platform via OAuth
 * Returns the OAuth redirect URL with access token
 */
export const authenticatePredict = async (email: string): Promise<string> => {
  const bearerToken = btoa(JSON.stringify({ email }));
  const response = await fetch("https://platform.datasurfr.ai/api/auth/predict", {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${bearerToken}`,
    },
  });
  if (!response.ok) {
    throw new Error(`Predict auth failed with status ${response.status}`);
  }
  const data = await response.json();
  if (!data?.accessToken) {
    throw new Error("Missing accessToken in Predict auth response");
  }
  const oauthUrl = new URL("https://platform.datasurfr.ai/login/oauth");
  oauthUrl.searchParams.set("token", data.accessToken);
  return oauthUrl.toString();
};

